import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import { createOperationLogApi } from './operationLogApi';
import { canReadOperationLogs } from './operationLogAuthorization';
import { createLocalDemoOperationLogApi } from './operationLogDemo';
import type { OperationLogPort } from './operationLogPort';

type OperationLogPageRuntime = {
  api: OperationLogPort;
  canRead: boolean;
  offlineDemoEnabled: boolean;
};

const RuntimeContext = createContext<OperationLogPageRuntime | null>(null);

export function OperationLogPageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: OperationLogPageRuntime;
}) {
  return (
    <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>
  );
}

export function ActiveOperationLogPageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, quoteTransport, offlineDemoEnabled } = useActiveSession();
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo(
    () => ({
      api: offlineDemoEnabled
        ? createLocalDemoOperationLogApi()
        : createOperationLogApi(quoteTransport),
      canRead:
        offlineDemoEnabled || canReadOperationLogs(ready?.user.permissions),
      offlineDemoEnabled,
    }),
    [offlineDemoEnabled, quoteTransport, ready],
  );
  return (
    <RuntimeContext.Provider value={ready ? value : null}>
      {children}
    </RuntimeContext.Provider>
  );
}

export function useOperationLogPageRuntime(): OperationLogPageRuntime {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error('OperationLogPageRuntimeProvider is required');
  return runtime;
}

export function useOptionalOperationLogPageRuntime(): OperationLogPageRuntime | null {
  return useContext(RuntimeContext);
}
